import { useState, useEffect, useMemo, useCallback } from "react";
import { VirtuosoMasonry } from "@virtuoso.dev/masonry";
import { LibraryItemPreview } from "../LibraryItemPreview/LibraryItemPreview.jsx";
import TagCloud from "../TagCloud/TagCloud.jsx";
import HintBox from "../../../../design-system/components/HintBox/HintBox.jsx";
import styles from "../../library.css.ts";

const getSparkScorer = (sparkFilter, spark) => {
  const tagPositions = {};
  if (sparkFilter.has(spark.id)) {
    spark.tags.forEach((t, idx) => {
      tagPositions[t] = idx;
    });
  }
  return (item) =>
    item.tags.reduce((prevTagScore, t) => {
      const pos = tagPositions[t] !== undefined ? tagPositions[t] : 100;
      return prevTagScore + 1 / Math.pow(pos + 1, 2);
    }, 1);
};
const itemToType = (item) => item.content.__typename;
const findBestTags = ({ sparkFilter, spark, items }) => {
  const tagCounts = {};
  const sparkTags = new Set([]);
  if (sparkFilter.has(spark.id)) {
    spark.tags.forEach((tag) => sparkTags.add(tag));
  }
  // consider only items which have at least one tag in common with selected spark
  const validItems = !sparkTags.size
    ? items
    : items.filter((item) => item.tags.some((tag) => sparkTags.has(tag)));
  validItems.forEach((item) => {
    item.tags.forEach((tag) => (tagCounts[tag] = (tagCounts[tag] || 0) + 1));
  });
  const tagsWithCounts = Object.entries(tagCounts);
  tagsWithCounts.sort((tc1, tc2) => tc2[1] - tc1[1]);
  return tagsWithCounts.slice(0, 7).map(([tag]) => tag);
};
/**
 * Custom React hook for managing a single tag selection state.
 *
 * This hook maintains a tag value that can be toggled on/off. When a tag is selected,
 * it returns a Set containing that tag. When toggled again with the same value, it
 * deselects the tag and returns an empty Set. This allows for single-selection filter
 * behavior in the library interface.
 *
 * @param {string|null} initial - The initial tag value to set, or null for no selection
 * @returns {[Set<string>, Function]} A tuple containing:
 *   - set: A Set containing the currently selected tag (or empty Set if none selected)
 *   - toggle: A function that toggles the tag selection on/off
 */
const useTag = (initial = null) => {
  const [data, setData] = useState(initial);
  const toggle = useCallback(
    (val) => setData((prev) => (prev === val ? null : val)),
    [],
  );
  const set = useMemo(() => {
    return data ? new Set([data]) : new Set();
  }, [data]);
  return [set, toggle];
};
const ButtonRow = ({
  validTags,
  tagFilter,
  toggleTag,
  validTypes,
  typeFilter,
  toggleType,
  validCategories,
  categoryFilter,
  toggleCategory,
  spark,
  toggleSpark,
  sparkFilter,
}) => {
  return (
    <div className={styles.buttonList}>
      <HintBox
        header="Hinweis: Externe Inhalte"
        text={`Die folgenden Inhalte stammen teilweise von externen Anbietern. Wir
          haben sie sorgfältig für euch ausgewählt und überprüfen sie
          regelmäßig. Trotzdem können wir nicht für alle Inhalte eine Garantie
          übernehmen.`}
      />
      <TagCloud
        validTags={validTags}
        tagFilter={tagFilter}
        toggleTag={toggleTag}
        validTypes={validTypes}
        typeFilter={typeFilter}
        toggleType={toggleType}
        validCategories={validCategories}
        categoryFilter={categoryFilter}
        toggleCategory={toggleCategory}
        spark={spark}
        sparkFilter={sparkFilter}
        toggleSpark={toggleSpark}
        sparkId={spark.id}
      />
    </div>
  );
};
export const ItemList = ({ items, spark }) => {
  const [sparkFilter, toggleSpark] = useTag(spark.id);
  const [tagFilter, toggleTag] = useTag();
  const [typeFilter, toggleType] = useTag();
  const [categoryFilter, toggleCategory] = useTag();

  /**
   * Retrieves the available height for the virtuoso component.
   * As the height of the page is flexible and there is also a header involved on the library page,
   * but outside the virtuoso, we need to get the virtuoso height dynamically.
   *
   * @returns {number} The height of the page content.
   */
  const calculateHeight = () => {
    const page = document.getElementById("page");
    const libraryHeader = document.getElementById("overlayHeader");
    const buffer = 3; // Borders

    if (page) {
      if (libraryHeader) {
        return page.offsetHeight - libraryHeader.offsetHeight - buffer;
      } else {
        return page.offsetHeight - buffer;
      }
    } else {
      return 300; // default height if page is not found
    }
  };

  const calculateWidth = () => {
    const page = document.getElementById("page");

    if (page) {
      return page.offsetWidth;
    } else {
      return 1440; // default width if page is not found
    }
  };

  const [height, setHeight] = useState(() => calculateHeight());
  const [width, setWidth] = useState(() => calculateWidth());

  const scorers = useMemo(() => {
    const res = [];
    if (sparkFilter.size) {
      res.push(getSparkScorer(sparkFilter, spark));
    }
    if (tagFilter.size) {
      res.push(
        (item) => item.tags.filter((t) => tagFilter.has(t)).length * 1000 || 1,
      );
    }
    if (typeFilter.size) {
      res.push((item) => (typeFilter.has(itemToType(item)) ? 1000 : 1));
    }
    if (categoryFilter.size) {
      res.push(
        (item) =>
          item.categories.filter((t) => categoryFilter.has(t)).length * 1000 ||
          1,
      );
    }
    return res;
  }, [sparkFilter, tagFilter, typeFilter, categoryFilter, spark]);

  const itemWithScores = useMemo(() => {
    const res = items.map((item) => ({
      item,
      score: scorers.reduce((prev, scoreFn) => prev * scoreFn(item), 1),
    }));
    res.sort((i1, i2) => i2.score - i1.score);
    return res;
  }, [items, scorers]);

  const data = useMemo(
    () => itemWithScores.map(({ item }) => item),
    [itemWithScores],
  );

  const validTypes = useMemo(() => {
    const res = new Set();
    items.forEach((i) => res.add(itemToType(i)));
    return res;
  }, [items]);

  const validCategories = useMemo(() => {
    const res = new Set();
    items.forEach((i) => i.categories.forEach((c) => res.add(c)));
    return res;
  }, [items]);

  const bestTags = findBestTags({ sparkFilter, spark, items });
  const bestTagSet = new Set(bestTags);
  const nonBestSelectedTags = [...tagFilter].filter((t) => !bestTagSet.has(t));
  const validTags = useMemo(
    () => [
      ...bestTags.slice(0, 7 - nonBestSelectedTags.length),
      ...nonBestSelectedTags,
    ],
    [bestTags, nonBestSelectedTags],
  );

  const columnCount = useMemo(
    () => Math.max(Math.ceil(width / 480), 1),
    [width],
  );

  useEffect(() => {
    const handleResize = () => {
      const newHeight = calculateHeight();
      setHeight(newHeight);

      const newWidth = calculateWidth();
      setWidth(newWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []); // The empty array ensures this effect runs only on mount and unmount

  return (
    <div
      style={{
        height: `${height}px`,
        width: `${width}px`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ButtonRow
        validTags={validTags}
        tagFilter={tagFilter}
        toggleTag={toggleTag}
        validTypes={validTypes}
        typeFilter={typeFilter}
        toggleType={toggleType}
        validCategories={validCategories}
        categoryFilter={categoryFilter}
        toggleCategory={toggleCategory}
        spark={spark}
        toggleSpark={toggleSpark}
        sparkFilter={sparkFilter}
      />
      <VirtuosoMasonry
        columnCount={columnCount}
        data={data}
        context={{
          tagFilter,
          toggleTag,
        }}
        ItemContent={LibraryItemPreview}
        // The masonry creates its own `overflow-y:auto` scroller; give it a
        // bounded height (fill the remaining flex space) so it virtualizes
        // instead of growing to fit every item.
        style={{ flex: 1, minHeight: 0 }}
      />
    </div>
  );
};
